import { useEffect, useMemo, useState } from 'react';

import { Select, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { patientsApiSlice } from '@/store/slices/api/patientsApiSlice';
import { useUpdateDeviceMutation } from '@/store/slices/api/transmissionReport/transmissionReportsApiSlice';
import { useUpdatePatientMutation } from '@/store/slices/api/transmissionReport/reportsPatientApiSlice';
import { useAppDispatch } from '@/store/hooks';
import { MatchDevice } from '@/types/matchDevice';
import { Patient } from '@/types/patient';
import { API_SLICE } from '@/utils/constants/apiEndpoints';
import { deviceStatusCode } from '@/utils/deviceStatus';
import CustomModal from '@/ui/Modal';

interface ReactivatePatientModalProps {
  opened: boolean;
  onClose: () => void;
  patientId: string;
  patientName: string;
  devices?: MatchDevice[];
}

function isInactiveDevice(device: MatchDevice): boolean {
  return deviceStatusCode(device.status) === 0;
}

function isActiveDevice(device: MatchDevice): boolean {
  return deviceStatusCode(device.status) === 1;
}

function deviceRecency(device: MatchDevice): number {
  const raw = device.lastTransmissionAt || device.implantDate || device.deviceImplantDate || device.createdAt;
  const time = raw ? new Date(raw).getTime() : 0;
  return Number.isFinite(time) ? time : 0;
}

function formatDeviceDate(value: string | Date): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function deviceOptionLabel(device: MatchDevice): string {
  const status = isActiveDevice(device) ? 'Active' : 'Inactive';
  const name = [status, device.type, device.modelName || device.serialNumber].filter(Boolean).join(' · ');
  if (device.lastTransmissionAt) {
    const when = formatDeviceDate(device.lastTransmissionAt);
    return when ? `${name} · Last transmission ${when}` : name;
  }
  const implanted = device.implantDate || device.deviceImplantDate;
  if (implanted) {
    const when = formatDeviceDate(implanted);
    return when ? `${name} · Implanted ${when}` : name;
  }
  const added = device.createdAt ? formatDeviceDate(device.createdAt) : '';
  return added ? `${name} · Added ${added}` : name;
}

export function ReactivatePatientModal({
  opened,
  onClose,
  patientId,
  patientName,
  devices = [],
}: ReactivatePatientModalProps) {
  const dispatch = useAppDispatch();
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [updatePatient] = useUpdatePatientMutation();
  const [updateDevice] = useUpdateDeviceMutation();

  const deviceChoices = useMemo(
    () =>
      devices
        .filter((device) => isActiveDevice(device) || isInactiveDevice(device))
        .sort((left, right) => deviceRecency(right) - deviceRecency(left)),
    [devices],
  );
  const hasActiveDevice = deviceChoices.some(isActiveDevice);
  const hasInactiveDevice = deviceChoices.some(isInactiveDevice);

  useEffect(() => {
    if (!opened) {
      setDeviceId(null);
      return;
    }
    const preferred = deviceChoices.find(isActiveDevice) ?? deviceChoices[0];
    setDeviceId(preferred?.id ?? null);
  }, [opened, deviceChoices]);

  const handleSubmit = async () => {
    const device = deviceChoices.find((item) => item.id === deviceId);
    if (!device || isSaving) return;
    const alreadyActive = isActiveDevice(device);
    if (!alreadyActive && (!device.serialNumber?.trim() || !device.vendor)) {
      notifications.show({
        title: 'Error',
        message: 'That device is missing a vendor or serial number, so it cannot be set active.',
        color: 'red',
      });
      return;
    }
    setIsSaving(true);
    try {
      if (!alreadyActive) {
        await updateDevice({
          patientId,
          reportId: undefined,
          id: device.id,
          vendor: device.vendor,
          serialNumber: device.serialNumber,
          modelNumber: device.modelNumber,
          modelName: device.modelName,
          type: device.type,
          status: 'Active',
        }).unwrap();
      }
      await updatePatient({
        patient: {
          id: patientId,
          patientStatus: 'Active',
          status: 'Active' as unknown as Patient['status'],
          statusReason: null,
          statusStartDate: null,
          statusEndDate: null,
        },
      }).unwrap();
      dispatch(
        patientsApiSlice.util.invalidateTags([
          { type: API_SLICE.Patient, id: patientId },
          { type: API_SLICE.Patient, id: `${patientId}-schedules` },
        ]),
      );
      notifications.show({
        title: 'Patient activated',
        message: alreadyActive
          ? `${patientName} is active. ${device.modelName || 'The selected device'} stays active.`
          : `${patientName} is active. ${device.modelName || 'The selected device'} is active.`,
        color: 'teal',
      });
      onClose();
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to reactivate the patient.',
        color: 'red',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <CustomModal
      header={{ title: 'Activate Patient' }}
      modalProps={{ opened, onClose, size: 'md' }}
      secondaryButton={{ text: 'Cancel', onClick: onClose }}
      primaryButton={{
        text: 'Activate Patient',
        onClick: handleSubmit,
        isDisabled: !deviceId,
        isLoading: isSaving,
      }}>
      <Stack gap="md" p="md">
        <Text size="sm" c="dimmed">
          {hasActiveDevice && hasInactiveDevice
            ? `Confirm the device for ${patientName}. The active device can stay active. Choosing an inactive device makes that device active. The most recently transmitting device is listed first.`
            : hasActiveDevice
              ? `This patient has no inactive device to reactivate. Confirm the active device for ${patientName}.`
              : hasInactiveDevice
                ? `Choose the inactive device to make active with ${patientName}. The most recently transmitting device is listed first. Last transmission, then implant date, decides the order.`
                : 'This patient has no device to confirm.'}
        </Text>
        {deviceChoices.length === 0 ? null : (
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              Device <Text span c="red">*</Text>
            </Text>
            <Select
              placeholder="Select a device..."
              data={deviceChoices.map((device) => ({
                value: device.id,
                label: deviceOptionLabel(device),
              }))}
              value={deviceId}
              onChange={setDeviceId}
              allowDeselect={false}
            />
          </Stack>
        )}
      </Stack>
    </CustomModal>
  );
}
